import React, { useState, useEffect, ChangeEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import TextInput from "../../../../components/ui/TextInput";
import Button from "../../../../components/ui/Button";
import Dropdown from "../../../../components/common/Dropdown";
import FormGroup from "../../../../components/common/FormGroup";
import ProviderChangeModal from "../../../../components/modals/ProviderChangeModal";
import {
    createScanConfig,
    getScanConfig,
    updateScanConfig,
    ProviderChangeImpact,
    ImageRecognitionProvider,
    ScanConfigResponse,
    WeaviateIndexStatus,
} from "../../../../api/progression";
import { getAdminInfo } from "../../../../api";
import { useNotification } from "../../../../context/NotificationContext";
import { getCurrentVariant } from "../../../../utils/variantOverrides";
import stepStyles from "../createStep.module.css";

const REWIND_COMPANY_ID = "rewinduniqueid";

function providerLabel(provider: ImageRecognitionProvider, t: any): string {
    if (provider === "HORAMA") return "Horama";
    if (provider === "WEAVIATE") return t("weaviateImageRecognition");
    return t("standardImageRecognition");
}

interface MainThemeProps {
    navigateToNext: () => void;
}

const MainTheme: React.FC<MainThemeProps> = ({ navigateToNext }) => {
    const { trackData, updateTrackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const token = localStorage.getItem("token") || "";

    const [themeName, setThemeName] = useState<string>("");
    const [originalThemeName, setOriginalThemeName] = useState<string>("");
    const [scanConfigId, setScanConfigId] = useState<string | null>(null);
    const isThoiry = useMemo(() => getCurrentVariant() === "thoiry", []);
    const { showNotification } = useNotification();
    const defaultProvider: ImageRecognitionProvider = isThoiry ? "HORAMA" : "OPENAI";
    const [imageRecognitionProvider, setImageRecognitionProvider] = useState<ImageRecognitionProvider>(defaultProvider);
    const [originalImageRecognitionProvider, setOriginalImageRecognitionProvider] =
        useState<ImageRecognitionProvider>(defaultProvider);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasHoramaConfig, setHasHoramaConfig] = useState<boolean>(false);
    const [isOwner, setIsOwner] = useState<boolean>(false);
    const [isRewindCompany, setIsRewindCompany] = useState<boolean>(false);

    // Provider change confirmation state
    const [showProviderWarning, setShowProviderWarning] = useState<boolean>(false);
    const [providerChangeImpact, setProviderChangeImpact] = useState<ProviderChangeImpact | null>(null);
    const [resyncing, setResyncing] = useState<boolean>(false);
    const [weaviateIndex, setWeaviateIndex] = useState<WeaviateIndexStatus | null>(null);

    useEffect(() => {
        const checkCompanyCapabilities = async () => {
            try {
                const adminInfo = await getAdminInfo({ token });
                if (adminInfo?.cmpany?.horamaConfig) {
                    setHasHoramaConfig(true);
                }
                if (adminInfo?.isOwner) {
                    setIsOwner(true);
                }
                if (adminInfo?.cmpany?.id === REWIND_COMPANY_ID) {
                    setIsRewindCompany(true);
                }
            } catch (error) {
                console.error("Error checking company capabilities:", error);
            }
        };
        checkCompanyCapabilities();
    }, [token]);

    // WEAVIATE is a Rewind-only pilot, gated on owner + Rewind company so the CMS
    // matches the backend guard (assertWeaviateProviderAllowed) and Category.tsx.
    const canUseWeaviate = isOwner && isRewindCompany;

    const providerOptions = useMemo(() => {
        if (isThoiry) {
            const options = [{ value: "HORAMA", label: "Horama" }];
            if (isOwner) {
                options.push({
                    value: "OPENAI",
                    label: t("standardImageRecognition") || "Reconnaissance d'image standard",
                });
            }
            if (canUseWeaviate) {
                options.push({ value: "WEAVIATE", label: t("weaviateImageRecognition") });
            }
            return options;
        }
        const options = [
            { value: "OPENAI", label: t("standardImageRecognition") || "Reconnaissance d'image standard" },
        ];
        if (hasHoramaConfig) {
            options.push({ value: "HORAMA", label: "Horama" });
        }
        if (canUseWeaviate) {
            options.push({ value: "WEAVIATE", label: t("weaviateImageRecognition") });
        }
        return options;
    }, [hasHoramaConfig, isThoiry, isOwner, canUseWeaviate, t]);

    // A config already on a provider the current user may no longer pick must still
    // render as the selected value rather than leaving the dropdown blank.
    const visibleProviderOptions = useMemo(() => {
        if (providerOptions.some((o) => o.value === imageRecognitionProvider)) return providerOptions;
        return [
            ...providerOptions,
            { value: imageRecognitionProvider, label: providerLabel(imageRecognitionProvider, t) },
        ];
    }, [providerOptions, imageRecognitionProvider, t]);

    const showProviderSelect = hasHoramaConfig || canUseWeaviate || isThoiry;

    useEffect(() => {
        const loadScanConfig = async () => {
            if (!trackData.trackId) return;

            try {
                const response = await getScanConfig({ token, trackId: trackData.trackId });
                setWeaviateIndex(response?.weaviateIndex ?? null);
                if (response?.scanConfig) {
                    setScanConfigId(response.scanConfig.id);
                    const localizedTheme = response.scanConfig.scanConfigLocalized?.find(
                        (loc) => loc.language === trackData.language?.toUpperCase(),
                    );
                    if (localizedTheme) {
                        setThemeName(localizedTheme.themeName);
                        setOriginalThemeName(localizedTheme.themeName);
                    }
                    if (response.scanConfig.imageRecognitionProvider) {
                        const provider = response.scanConfig.imageRecognitionProvider as ImageRecognitionProvider;
                        setImageRecognitionProvider(provider);
                        setOriginalImageRecognitionProvider(provider);
                    }
                    updateTrackData("scanConfig", {
                        id: response.scanConfig.id,
                        themeName: localizedTheme?.themeName || "",
                        referenceImageUrl: response.scanConfig.referenceImageUrl,
                        imageRecognitionProvider: response.scanConfig.imageRecognitionProvider,
                    });
                }
            } catch (error) {
                console.error("Error loading scan config:", error);
            }
        };

        loadScanConfig();
    }, [trackData.trackId, trackData.language, token]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setThemeName(e.target.value);
    };

    // The endpoint returns 200 even when Weaviate indexing failed or skipped objects, so
    // surface it here — otherwise the track silently ships with missing vectors and only
    // fails later, in the mobile app.
    const notifyWeaviateOutcome = (response: ScanConfigResponse | ProviderChangeImpact) => {
        if ("weaviateWarning" in response && response.weaviateWarning) {
            showNotification({
                message: `${t("weaviateIndexIncomplete")} — ${response.weaviateWarning}`,
                type: "warning",
                duration: 20000,
            });
        } else if ("weaviateSync" in response && response.weaviateSync) {
            showNotification({
                message: t("weaviateIndexed", { n: response.weaviateSync.indexed }),
                type: "success",
            });
        }
    };

    // Indexing otherwise only runs on a provider switch: if that single run fails, the track
    // sits on WEAVIATE with no vectors and nothing in the UI can retry it. Safe to replay —
    // the sync clears the track's vectors before re-indexing.
    // Re-read the count rather than trusting the sync's own tally: the badge must reflect
    // what Weaviate actually holds, which is the whole point of showing it.
    const refreshIndexStatus = async () => {
        if (!trackData.trackId) return;
        try {
            const response = await getScanConfig({ token, trackId: trackData.trackId });
            setWeaviateIndex(response?.weaviateIndex ?? null);
        } catch (error) {
            console.error("Error refreshing Weaviate index status:", error);
        }
    };

    const handleResync = async () => {
        if (!scanConfigId) return;

        setResyncing(true);
        try {
            const response = await updateScanConfig({
                token,
                scanConfigId,
                language: trackData.language || "EN",
                resyncWeaviate: true,
            });
            notifyWeaviateOutcome(response);
            await refreshIndexStatus();
        } catch (error) {
            console.error("Error re-indexing scan config:", error);
            showNotification({
                message: error instanceof Error ? error.message : String(error),
                type: "error",
            });
        } finally {
            setResyncing(false);
        }
    };

    const handleSave = async (confirmProviderChange = false) => {
        if (!trackData.trackId) return;

        const themeNameChanged = themeName !== originalThemeName;
        const providerChanged = imageRecognitionProvider !== originalImageRecognitionProvider;
        const hasChanges = themeNameChanged || providerChanged;

        if (scanConfigId && !hasChanges) {
            navigateToNext();
            return;
        }

        setLoading(true);
        try {
            if (scanConfigId) {
                const response = await updateScanConfig({
                    token,
                    scanConfigId,
                    themeName: themeNameChanged ? themeName : undefined,
                    language: trackData.language || "EN",
                    imageRecognitionProvider: providerChanged ? imageRecognitionProvider : undefined,
                    confirmProviderChange,
                });

                if ("requiresConfirmation" in response && response.requiresConfirmation) {
                    setProviderChangeImpact(response as ProviderChangeImpact);
                    setShowProviderWarning(true);
                    setLoading(false);
                    return;
                }

                if (themeNameChanged) setOriginalThemeName(themeName);
                if (providerChanged) setOriginalImageRecognitionProvider(imageRecognitionProvider);

                notifyWeaviateOutcome(response);
            } else {
                const response = await createScanConfig({
                    token,
                    trackId: trackData.trackId,
                    themeName,
                    language: trackData.language || "EN",
                    imageRecognitionProvider,
                });
                if (response?.scanConfig) {
                    setScanConfigId(response.scanConfig.id);
                    setOriginalThemeName(themeName);
                    setOriginalImageRecognitionProvider(imageRecognitionProvider);
                    updateTrackData("scanConfig", {
                        id: response.scanConfig.id,
                        themeName,
                        imageRecognitionProvider,
                    });
                }
            }
            setShowProviderWarning(false);
            setProviderChangeImpact(null);
            navigateToNext();
        } catch (error) {
            console.error("Error saving scan config:", error);
            showNotification({
                message: error instanceof Error ? error.message : String(error),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmProviderChange = () => {
        handleSave(true);
    };

    const handleCancelProviderChange = () => {
        setImageRecognitionProvider(originalImageRecognitionProvider);
        setShowProviderWarning(false);
        setProviderChangeImpact(null);
    };

    const providerDescription =
        imageRecognitionProvider === "WEAVIATE"
            ? t("weaviateProviderDescription")
            : imageRecognitionProvider === "OPENAI"
              ? t("standardProviderDescription") ||
                "Utilise l'IA pour la reconnaissance d'image. Les clés de reconnaissance sont automatiquement définies à partir du nom anglais de l'objet."
              : t("horamaProviderDescription") ||
                "Utilise Horama pour la reconnaissance d'image. Les clés de reconnaissance doivent être configurées manuellement pour chaque objet.";

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${stepStyles.stepScanTheme}`}>
            <TitleSubtitle
                title={t("whatIsTheMainTheme")}
                subtitle={t("defineWhatUsersWillScan")}
                tooltip={t("doNotTranslateTooltip")}
                layout="scanTheme"
            />

            <div className={stepStyles.stepDescription}>
                <FormGroup title={t("themeName") || "Theme Name"} required>
                    <TextInput
                        placeholder={t("enterThemeName")}
                        value={themeName}
                        onChange={handleChange}
                        size="medium"
                    />
                </FormGroup>

                {showProviderSelect && (
                    <FormGroup title={t("imageRecognitionProvider") || "Fournisseur de reconnaissance d'image"}>
                        <Dropdown
                            label=""
                            options={visibleProviderOptions}
                            value={imageRecognitionProvider}
                            onChange={(value) => setImageRecognitionProvider(value as ImageRecognitionProvider)}
                            placeholder={t("selectProvider") || "Sélectionnez un fournisseur"}
                            loading={false}
                            error={false}
                            fullWidth
                            isWithoutMargin
                        />
                        <p className={stepStyles.providerNote}>{providerDescription}</p>
                        {weaviateIndex && originalImageRecognitionProvider === "WEAVIATE" && (
                            <div className={stepStyles.weaviateIndexStatus}>
                                {weaviateIndex.indexed === null ? (
                                    <span className={stepStyles.indexWarn}>⚠ {t("weaviateIndexUnknown")}</span>
                                ) : weaviateIndex.indexed >= weaviateIndex.expected ? (
                                    <span className={stepStyles.indexOk}>
                                        ✓{" "}
                                        {t("weaviateIndexStatus", {
                                            indexed: weaviateIndex.indexed,
                                            expected: weaviateIndex.expected,
                                        })}
                                    </span>
                                ) : (
                                    <span className={stepStyles.indexWarn}>
                                        ⚠{" "}
                                        {t("weaviateIndexStatus", {
                                            indexed: weaviateIndex.indexed,
                                            expected: weaviateIndex.expected,
                                        })}
                                    </span>
                                )}
                                {canUseWeaviate &&
                                    scanConfigId &&
                                    (weaviateIndex.indexed === null ||
                                        weaviateIndex.indexed < weaviateIndex.expected) && (
                                        <Button
                                            label={resyncing ? t("weaviateResyncing") : t("weaviateResync")}
                                            onClick={handleResync}
                                            disabled={resyncing}
                                            variant="outline"
                                            shape="pill"
                                            isSmall
                                        />
                                    )}
                            </div>
                        )}
                    </FormGroup>
                )}
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={loading ? t("saving") : t("continue")}
                    onClick={() => handleSave(false)}
                    disabled={!themeName || loading}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>

            <ProviderChangeModal
                isOpen={showProviderWarning}
                onClose={handleCancelProviderChange}
                onConfirm={handleConfirmProviderChange}
                impact={providerChangeImpact}
                loading={loading}
            />
        </div>
    );
};

export default MainTheme;
